import { useEffect, useRef } from 'react';

import eventHandler from './eventHandler';
import { getAccessTokenThatMinValidForNMinutes } from 'apis/api';
import { MAIN_WS_URL } from 'core/endpoints';
import { getReconnectDelay, TOKEN_PADDING_MINUTES } from './constents';

const WebSocketComponent = ({ profile }: { profile: any }) => {
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef<number>(0);

  useEffect(() => {
    if (profile) {
      connectWebSocket();

      return () => {
        cleanup();
      };
    }
  }, [profile]);

  const cleanup = () => {
    if (ws.current) {
      ws.current.onclose = null; // Prevent triggering reconnection on manual close
      ws.current.close();
    }
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    reconnectAttempts.current = 0;
  };

  const handleWsEvent = (event: MessageEvent) => {
    eventHandler(event);
  };

  const connectWebSocket = async () => {
    try {
      const { ephemeralToken } = await getAccessTokenThatMinValidForNMinutes(TOKEN_PADDING_MINUTES);
      ws.current = new WebSocket(`${MAIN_WS_URL}?token=${ephemeralToken}`);

      ws.current.onopen = () => {
        // Reset reconnect attempts on successful connection
        reconnectAttempts.current = 0;
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
          reconnectTimeout.current = null;
        }
      };

      ws.current.onmessage = handleWsEvent;

      ws.current.onclose = () => {
        attemptReconnect(); // Attempt to reconnect on close
      };

      ws.current.onerror = () => {
        ws.current?.close(); // Ensure the socket is closed in case of error
      };
    } catch (error) {
      // console.error('Failed to connect WebSocket:', error);
      attemptReconnect();
    }
  };

  const attemptReconnect = () => {
    if (!reconnectTimeout.current) {
      const delay = getReconnectDelay(reconnectAttempts.current);
      reconnectAttempts.current += 1;

      reconnectTimeout.current = setTimeout(() => {
        reconnectTimeout.current = null;
        connectWebSocket();
      }, delay);
    }
  };

  return null;
};

export default WebSocketComponent;
